import type React from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from '@/providers/auth-provider'
import { RequireAuth, RequireOnboarding, RedirectIfAuthenticated } from '@/components/route-guards'
import { OrgLayout } from '@/layouts/org-layout'
import { LoginPage } from '@/pages/login'
import { SignupPage } from '@/pages/signup'
import { OnboardingProfilePage } from '@/pages/onboarding-profile'
import { OnboardingOrgPage } from '@/pages/onboarding-org'
import { InvitePage } from '@/pages/invite'
import { PlansListPage } from '@/pages/plans-list'
import { PlanDetailPage } from '@/pages/plan-detail'
import { DefaultRedirect } from '@/components/default-redirect'
import { WelcomePage } from '@/pages/take/welcome'
import { RespondentChatPage } from '@/pages/take/chat'

export function App(): React.ReactElement {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<RedirectIfAuthenticated><LoginPage /></RedirectIfAuthenticated>} />
          <Route path="/signup" element={<RedirectIfAuthenticated><SignupPage /></RedirectIfAuthenticated>} />

          {/* Respondent routes (no auth guard — anonymous sign-in handled inside) */}
          <Route path="/c/:shareToken" element={<WelcomePage />} />
          <Route path="/c/:shareToken/:conversationId" element={<RespondentChatPage />} />

          {/* Invitation (requires auth for acceptance) */}
          <Route path="/invite/:token" element={<RequireAuth><InvitePage /></RequireAuth>} />

          {/* Onboarding (requires auth but NOT full onboarding) */}
          <Route path="/onboarding/profile" element={<RequireAuth><OnboardingProfilePage /></RequireAuth>} />
          <Route path="/onboarding/org" element={<RequireAuth><OnboardingOrgPage /></RequireAuth>} />

          {/* Org-scoped routes (requires auth + onboarding) */}
          <Route
            path="/:orgSlug"
            element={
              <RequireAuth>
                <RequireOnboarding>
                  <OrgLayout />
                </RequireOnboarding>
              </RequireAuth>
            }
          >
            <Route path="plans" element={<PlansListPage />} />
            <Route path="plans/:planId" element={<PlanDetailPage />} />
            <Route index element={<Navigate to="plans" replace />} />
          </Route>

          {/* Default redirect: resolve default org and go there */}
          <Route
            path="/"
            element={
              <RequireAuth>
                <RequireOnboarding>
                  <DefaultRedirect />
                </RequireOnboarding>
              </RequireAuth>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}
